import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Fragment, type ReactNode } from 'react';
import * as UI from '@siloreal:shared:ui';
import type { DropdownOptionBadgePreset } from '@siloreal:shared:ui/basic/dropdown';
import { useClosureActions } from '../../hook/use-closure-actions';
import type { SilobagListLogicState } from '../../hook/use-silobag-list-logic-state';
import { useHeaderExtractionExport } from '../../hook/use-header-extraction-export';
import { useHeaderQualitySampleActions } from '../../hook/use-header-quality-sample-actions';
import { useHeaderReportActions } from '../../hook/use-header-report-actions';
import { useHeaderSatelliteCertification } from '../../hook/use-header-satellite-certification';
import { LabTemplateModal } from '../lab-template-modal';
import { SatelliteReportModal } from '../satellite-report-modal';
import { SilobagClosureModal } from '../silobag-closure-modal';
import * as HOOKS from '@siloreal:shared:hooks';

type MenuAction = {
  key: string;
  label: string;
  icon: ReactNode;
  format?: DropdownOptionBadgePreset;
};

type MenuGroup = {
  key: string;
  title: string;
  actions: readonly MenuAction[];
};

const getMenuGroupsByView = (): Record<SilobagListLogicState['view'], readonly MenuGroup[]> => {
  const i18n = HOOKS.useLocale.getState();
  const SILOSPRO_QUALITY_SAMPLES_GROUP = {
    key: 'quality-samples',
    title: i18n.t('temp.common.quality_sample'),
    actions: [
      {
        key: 'upload-quality-samples',
        label: i18n.t('temp.private.list.common.actions.upload_spreadsheet'),
        icon: 'upload',
      },
      {
        key: 'download-quality-samples-combined',
        label: i18n.t('temp.private.list.common.actions.download_silospro_silobags_spreadsheet'),
        icon: 'download',
        format: 'excel',
      },
      {
        key: 'download-quality-samples-silospro',
        label: i18n.t('temp.private.list.common.actions.download_silospro_spreadsheet'),
        icon: 'download',
        format: 'excel',
      },
    ],
  } as const satisfies MenuGroup;

  const SILOBAG_QUALITY_SAMPLES_GROUP = {
    key: 'quality-samples',
    title: i18n.t('temp.common.quality_sample'),
    actions: [
      {
        key: 'upload-quality-samples',
        label: i18n.t('temp.private.list.common.actions.upload_spreadsheet'),
        icon: 'upload',
      },
      {
        key: 'download-quality-samples-combined',
        label: i18n.t('temp.private.list.common.actions.download_silospro_silobags_spreadsheet'),
        icon: 'download',
        format: 'excel',
      },
      {
        key: 'download-quality-samples-silobag',
        label: i18n.t('temp.private.list.common.actions.download_silobag_spreadsheet'),
        icon: 'download',
        format: 'excel',
      },
    ],
  } as const satisfies MenuGroup;

  const SILOSPRO_GLOBAL_EXPORTS_GROUP = {
    key: 'global-exports',
    title: i18n.t('temp.private.list.common.actions.global_exports'),
    actions: [
      {
        key: 'global-report-combined',
        label: i18n.t('temp.private.list.common.actions.silospro_silobags_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
      {
        key: 'global-report-silospro',
        label: i18n.t('temp.private.list.common.actions.silospro_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
      {
        key: 'global-extraction-report',
        label: i18n.t('temp.private.list.common.actions.extraction_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
      {
        key: 'global-satellite-certificate-silospro',
        label: i18n.t('temp.private.list.common.actions.silospro_satellite_certification'),
        icon: <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'pdf',
      },
    ],
  } as const satisfies MenuGroup;

  const SILOBAG_GLOBAL_EXPORTS_GROUP = {
    key: 'global-exports',
    title: i18n.t('temp.private.list.common.actions.global_exports'),
    actions: [
      {
        key: 'global-report-combined',
        label: i18n.t('temp.private.list.common.actions.silospro_silobags_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
      {
        key: 'global-report-silobag',
        label: i18n.t('temp.private.list.common.actions.silobag_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
      {
        key: 'global-extraction-report',
        label: i18n.t('temp.private.list.common.actions.extraction_report'),
        icon: <InsertChartOutlinedIcon sx={{ fontSize: 18 }} />,
        format: 'excel',
      },
    ],
  } as const satisfies MenuGroup;

  const CLOSURES_GROUP = {
    key: 'closures',
    title: i18n.t('temp.common.closures'),
    actions: [
      {
        key: 'upload-closures',
        label: i18n.t('temp.private.list.common.actions.upload_spreadsheet'),
        icon: 'upload',
      },
      {
        key: 'download-closures-template',
        label: i18n.t('temp.private.list.common.actions.download_model_spreadsheet'),
        icon: 'download',
        format: 'excel',
      },
    ],
  } as const satisfies MenuGroup;

  return {
    silospro: [SILOSPRO_QUALITY_SAMPLES_GROUP, SILOSPRO_GLOBAL_EXPORTS_GROUP, CLOSURES_GROUP],
    silobag: [SILOBAG_QUALITY_SAMPLES_GROUP, SILOBAG_GLOBAL_EXPORTS_GROUP, CLOSURES_GROUP],
  };
};

type Props = {
  view: SilobagListLogicState['view'];
  tid: string;
  popupTid: string;
};

export function HeaderActionsDropdown({ view, tid, popupTid }: Props) {
  const i18n = HOOKS.useLocale();
  const groups = getMenuGroupsByView()[view];
  const closures = useClosureActions();
  const extractionReport = useHeaderExtractionExport();
  const qualitySamples = useHeaderQualitySampleActions();
  const reports = useHeaderReportActions();
  const satelliteCertification = useHeaderSatelliteCertification();
  const isQualitySampleActionPending =
    qualitySamples.upload.isUploading || qualitySamples.download.isResolving;
  const actionBehavior: Partial<Record<string, { onClick: () => void; disabled: boolean }>> = {
    'upload-quality-samples': {
      onClick: qualitySamples.upload.openFilePicker,
      disabled: isQualitySampleActionPending,
    },
    'download-quality-samples-combined': {
      onClick: qualitySamples.openCombinedTemplates,
      disabled: isQualitySampleActionPending,
    },
    'download-quality-samples-silospro': {
      onClick: qualitySamples.openSilosProTemplates,
      disabled: isQualitySampleActionPending,
    },
    'download-quality-samples-silobag': {
      onClick: qualitySamples.openSilobagTemplates,
      disabled: isQualitySampleActionPending,
    },
    'global-report-combined': {
      onClick: reports.downloadCombined,
      disabled: reports.isExporting,
    },
    'global-report-silospro': {
      onClick: reports.downloadSilosPro,
      disabled: reports.isExporting,
    },
    'global-report-silobag': {
      onClick: reports.downloadSilobags,
      disabled: reports.isExporting,
    },
    'global-extraction-report': {
      onClick: extractionReport.download,
      disabled: extractionReport.isExporting,
    },
    'global-satellite-certificate-silospro': {
      onClick: satelliteCertification.generateForAllSilosPro,
      disabled: satelliteCertification.isGenerating,
    },
    'upload-closures': {
      onClick: closures.openFilePicker,
      disabled: closures.isUploading,
    },
    'download-closures-template': {
      onClick: closures.downloadTemplate,
      disabled: closures.isDownloading,
    },
  };

  return (
    <>
      <UI.dropdown.base
        variant="basic"
        label={i18n.t('temp.common.actions')}
        icon={<MoreVertIcon fontSize="small" />}
        iconPosition="end"
        ariaLabel="Abrir acciones"
        closeOnContentClick
        tid={tid}
        popupTid={popupTid}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        menuAriaLabel="Acciones del listado"
      >
        {groups.map((group) => (
          <Fragment key={group.key}>
            <UI.dropdown.section align="center">{group.title}</UI.dropdown.section>
            {group.actions.map((action) => {
              const behavior = actionBehavior[action.key];
              const format = 'format' in action ? action.format : undefined;

              return (
                <UI.dropdown.option
                  key={action.key}
                  icon={action.icon}
                  badge={format}
                  onClick={behavior?.onClick}
                  disabled={behavior?.disabled}
                  tid={`silobag-list-header-action-${action.key}`}
                >
                  {action.label}
                </UI.dropdown.option>
              );
            })}
          </Fragment>
        ))}
      </UI.dropdown.base>
      <input
        ref={qualitySamples.upload.fileInputRef}
        type="file"
        hidden
        accept={qualitySamples.upload.inputAccept}
        onChange={qualitySamples.upload.uploadQualitySamples}
        data-testid="silobag-list-quality-sample-file-input"
      />
      <input
        ref={closures.fileInputRef}
        type="file"
        hidden
        accept={closures.inputAccept}
        onChange={closures.uploadClosures}
        data-testid="silobag-list-closure-file-input"
      />
      {closures.closureIds ? (
        <SilobagClosureModal open silobagIds={closures.closureIds} onClose={closures.closeClosureModal} />
      ) : null}
      <LabTemplateModal
        open={qualitySamples.download.isModalOpen}
        silobagIds={qualitySamples.download.silobagIds}
        onClose={qualitySamples.download.closeModal}
      />
      <SatelliteReportModal
        open={satelliteCertification.isModalOpen}
        onClose={satelliteCertification.closeModal}
      />
    </>
  );
}
